import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiChevronDown } from 'react-icons/fi'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import CustomerSelectInput from '@/components/customers/CustomerSelectInput'
import StockReferenceSelectInput from '@/components/inventory/StockReferenceSelectInput'
import {
  createStockDispatch,
  updateStockDispatch,
  uploadDocument,
  populateFromOrder,
  WarehouseApi,
  StockDispatchStatus,
  DocumentType,
} from '@/services/api/stockDispatch'
import { getOrderFulfillments, OrderFulfillmentApi } from '@/services/api/orderFulfillments'

interface AddDispatchModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  warehouses?: WarehouseApi[]
  editDispatch?: {
    id: string
    orderFulfillmentId?: string | null
    warehouseId?: string | null
    product?: string | null
    lotNumber?: string | null
    qtyDispatched?: string | null
    customer?: string | null
    destination?: string | null
    dispatchDate?: string | null
    status?: StockDispatchStatus
    dispatchCosts?: string | null
    dispatchCostsCurrency?: string | null
  } | null
}

const AddDispatchModal = ({ isOpen, onClose, onSuccess, warehouses = [], editDispatch = null }: AddDispatchModalProps) => {
  const [formData, setFormData] = useState({
    orderFulfillmentId: '',
    warehouseId: '',
    product: '',
    lotNumber: '',
    qtyDispatched: '',
    customer: '',
    destination: '',
    dispatchDate: '',
    status: '' as StockDispatchStatus | '',
    dispatchCosts: '',
    dispatchCostsCurrency: 'USD',
  })

  const [orderFulfillments, setOrderFulfillments] = useState<OrderFulfillmentApi[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [uploadedFiles, setUploadedFiles] = useState<{
    dispatchInstruction?: File
    proofOfDelivery?: File
    transporterInvoice?: File
    salesInvoice?: File
  }>({})
  const [uploadingType, setUploadingType] = useState<DocumentType | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      getOrderFulfillments({ limit: 500 })
        .then((res) => setOrderFulfillments(res.orderFulfillments ?? []))
        .catch(() => {})
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !editDispatch) return
    setFormData({
      orderFulfillmentId: editDispatch.orderFulfillmentId ?? '',
      warehouseId: editDispatch.warehouseId ?? '',
      product: editDispatch.product ?? '',
      lotNumber: editDispatch.lotNumber ?? '',
      qtyDispatched: editDispatch.qtyDispatched ?? '',
      customer: editDispatch.customer ?? '',
      destination: editDispatch.destination ?? '',
      dispatchDate: editDispatch.dispatchDate ? String(editDispatch.dispatchDate).slice(0, 10) : '',
      status: editDispatch.status ?? '',
      dispatchCosts: editDispatch.dispatchCosts ?? '',
      dispatchCostsCurrency: editDispatch.dispatchCostsCurrency ?? 'USD',
    })
    setUploadedFiles({})
    setError(null)
  }, [isOpen, editDispatch])

  const handleOrderChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value
    setFormData((prev) => ({ ...prev, orderFulfillmentId: id }))
    setError(null)
    if (!id) return

    try {
      const data = await populateFromOrder(id)
      setFormData((prev) => ({
        ...prev,
        orderFulfillmentId: id,
        product: data.product || prev.product || '',
        lotNumber: data.lotNumber || prev.lotNumber || '',
        customer: data.customer || prev.customer || '',
        destination: data.destination || prev.destination || '',
        qtyDispatched: data.quantity || prev.qtyDispatched || '',
      }))
    } catch {
      // Silently ignore; user can fill manually
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError(null)
  }

  const handleFileClick = (type: DocumentType) => {
    setUploadingType(type)
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && uploadingType) {
      setUploadedFiles(prev => ({
        ...prev,
        [uploadingType]: file
      }))
    }
    setUploadingType(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const payload = {
        orderFulfillmentId: formData.orderFulfillmentId || undefined,
        warehouseId: formData.warehouseId || undefined,
        product: formData.product || undefined,
        lotNumber: formData.lotNumber || undefined,
        qtyDispatched: formData.qtyDispatched || undefined,
        customer: formData.customer || undefined,
        destination: formData.destination || undefined,
        dispatchDate: formData.dispatchDate || undefined,
        status: formData.status || undefined,
        dispatchCosts: formData.dispatchCosts || undefined,
        dispatchCostsCurrency: formData.dispatchCostsCurrency || undefined,
      }
      const dispatch = editDispatch
        ? await updateStockDispatch(editDispatch.id, payload)
        : await createStockDispatch(payload)

      // Upload any files
      const fileUploads = Object.entries(uploadedFiles).filter(([, file]) => file) as [string, File][]
      await Promise.all(
        fileUploads.map(async ([type, file]) => {
          try {
            await uploadDocument(dispatch.id, type as DocumentType, file)
          } catch (uploadErr) {
            console.error(`Failed to upload ${type}:`, uploadErr)
          }
        })
      )

      onSuccess?.()
      handleClose()
    } catch (err: any) {
      setError(err.message || (editDispatch ? 'Failed to update dispatch' : 'Failed to create dispatch'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      orderFulfillmentId: '',
      warehouseId: '',
      product: '',
      lotNumber: '',
      qtyDispatched: '',
      customer: '',
      destination: '',
      dispatchDate: '',
      status: '',
      dispatchCosts: '',
      dispatchCostsCurrency: 'USD',
    })
    setUploadedFiles({})
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end" onClick={handleClose} style={{ zIndex: 60 }}>
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '600px',
          maxWidth: '600px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px'
        }}
      >
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />

        <style>{`
          #add-dispatch-form input::placeholder,
          #add-dispatch-form select option[value=""] {
            color: #69697A !important;
            font-family: 'Lexend Deca';
            font-weight: 300;
            font-size: 12px;
          }
          #add-dispatch-form input:not(:placeholder-shown),
          #add-dispatch-form select:not([value=""]) {
            color: #212126 !important;
            font-family: 'Lexend Deca';
            font-weight: 500;
            font-size: 12px;
          }
        `}</style>

        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', lineHeight: '26px', color: '#212126' }}>
            {editDispatch ? 'Edit Dispatch' : 'Add Dispatch'}
          </h2>
          <button
            onClick={handleClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ width: '24px', height: '24px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form id="add-dispatch-form" onSubmit={handleSubmit} className="flex-1" style={{ padding: '24px', overflow: 'auto' }}>
          {error && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#E93323' }}>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                Link to Order Fulfillment
              </label>
              <div className="relative">
                <select
                  name="orderFulfillmentId"
                  value={formData.orderFulfillmentId}
                  onChange={handleOrderChange}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px', color: formData.orderFulfillmentId ? '#212126' : '#69697A', fontWeight: formData.orderFulfillmentId ? 500 : 300 }}
                >
                  <option value="">Select order (optional)</option>
                  {orderFulfillments.map((o) => (
                    <option key={o.id} value={o.id}>
                      {[o.customer, o.product, o.containerNumber, o.lotBatch].filter(Boolean).join(' — ') || o.id.slice(0, 8)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Warehouse
                </label>
                <div className="relative">
                  <select
                    name="warehouseId"
                    value={formData.warehouseId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                    style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px', color: formData.warehouseId ? '#212126' : '#69697A', fontWeight: formData.warehouseId ? 500 : 300 }}
                  >
                    <option value="">Select warehouse</option>
                    {warehouses.map((wh) => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                </div>
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Product
                </label>
                <ProductSelectInput
                  value={formData.product}
                  onChange={(product) => setFormData((prev) => ({ ...prev, product }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Lot Number
                </label>
                <StockReferenceSelectInput
                  type="lot"
                  value={formData.lotNumber}
                  onChange={(lotNumber) => setFormData((prev) => ({ ...prev, lotNumber }))}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Qty Dispatched
                </label>
                <input
                  type="text"
                  name="qtyDispatched"
                  value={formData.qtyDispatched}
                  onChange={handleChange}
                  placeholder="Enter quantity (e.g. 1000)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Customer
                </label>
                <CustomerSelectInput
                  value={formData.customer}
                  onChange={(customer) => setFormData((prev) => ({ ...prev, customer }))}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Destination
                </label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  placeholder="Enter destination"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Dispatch Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="dispatchDate"
                    value={formData.dispatchDate}
                    onChange={handleChange}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Status
                </label>
                <div className="relative">
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                    style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px', color: formData.status ? '#212126' : '#69697A', fontWeight: formData.status ? 500 : 300 }}
                  >
                    <option value="">Select status</option>
                    <option value="in_transit">In Transit</option>
                    <option value="completed">Completed</option>
                    <option value="rerouted">Rerouted</option>
                    <option value="in_progress">In Progress</option>
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '4px' }}>
                  Dispatch Costs (transport only)
                </label>
                <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '16px', color: '#9B9BA8', marginBottom: '8px' }}>
                  Enter transporter / haulage cost only — not warehouse, handling, or full transaction value.
                </p>
                <input
                  type="text"
                  name="dispatchCosts"
                  value={formData.dispatchCosts}
                  onChange={handleChange}
                  placeholder="Transport cost"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Currency
                </label>
                <div className="relative">
                  <select
                    name="dispatchCostsCurrency"
                    value={formData.dispatchCostsCurrency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                    style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px', color: '#212126', fontWeight: 500 }}
                  >
                    <option value="USD">USD</option>
                    <option value="ZAR">ZAR</option>
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                </div>
              </div>
            </div>

            {/* Document uploads */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Dispatch Instruction
                </label>
                <button
                  type="button"
                  onClick={() => handleFileClick('dispatchInstruction')}
                  className="w-full hover:opacity-90 transition-opacity"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: uploadedFiles.dispatchInstruction ? '#13AB5B' : '#212126',
                    backgroundColor: uploadedFiles.dispatchInstruction ? '#E8F7EF' : '#FFFFFF',
                    border: uploadedFiles.dispatchInstruction ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                    borderRadius: '8px',
                    height: '40px'
                  }}
                >
                  {uploadedFiles.dispatchInstruction ? uploadedFiles.dispatchInstruction.name.slice(0, 20) + '...' : 'Upload'}
                </button>
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Proof of Delivery
                </label>
                <button
                  type="button"
                  onClick={() => handleFileClick('proofOfDelivery')}
                  className="w-full hover:opacity-90 transition-opacity"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: uploadedFiles.proofOfDelivery ? '#13AB5B' : '#212126',
                    backgroundColor: uploadedFiles.proofOfDelivery ? '#E8F7EF' : '#FFFFFF',
                    border: uploadedFiles.proofOfDelivery ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                    borderRadius: '8px',
                    height: '40px'
                  }}
                >
                  {uploadedFiles.proofOfDelivery ? uploadedFiles.proofOfDelivery.name.slice(0, 20) + '...' : 'Upload'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Transporter Invoice
                </label>
                <button
                  type="button"
                  onClick={() => handleFileClick('transporterInvoice')}
                  className="w-full hover:opacity-90 transition-opacity"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: uploadedFiles.transporterInvoice ? '#13AB5B' : '#212126',
                    backgroundColor: uploadedFiles.transporterInvoice ? '#E8F7EF' : '#FFFFFF',
                    border: uploadedFiles.transporterInvoice ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                    borderRadius: '8px',
                    height: '40px'
                  }}
                >
                  {uploadedFiles.transporterInvoice ? uploadedFiles.transporterInvoice.name.slice(0, 20) + '...' : 'Upload'}
                </button>
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Sales Invoice
                </label>
                <button
                  type="button"
                  onClick={() => handleFileClick('salesInvoice')}
                  className="w-full hover:opacity-90 transition-opacity"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: uploadedFiles.salesInvoice ? '#13AB5B' : '#212126',
                    backgroundColor: uploadedFiles.salesInvoice ? '#E8F7EF' : '#FFFFFF',
                    border: uploadedFiles.salesInvoice ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                    borderRadius: '8px',
                    height: '40px'
                  }}
                >
                  {uploadedFiles.salesInvoice ? uploadedFiles.salesInvoice.name.slice(0, 20) + '...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </form>

        <div
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderRadius: '8px' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-dispatch-form"
            disabled={isSubmitting}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#FFFFFF', backgroundColor: '#212126', borderRadius: '8px' }}
          >
            {isSubmitting ? (editDispatch ? 'Saving...' : 'Adding...') : (editDispatch ? 'Save' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AddDispatchModal
